import { useSortable } from "@dnd-kit/sortable";
import React, { useState } from "react";
import { CSS } from "@dnd-kit/utilities";
import { TabType } from "../../../services/helpers/types";
import Button from "../Button/Button";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import classNames from "classnames";
import { DeleteOutline, Edit } from "@mui/icons-material";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import EditTab from "../EditTab/EditTab";
import { updateTab } from "../../../services/api/tabs.api";
import { useDispatch } from "react-redux";
import { sendToasterMessage } from "../../../services/helpers/commonHelpers";
import { MessageTypeEnum } from "../../../redux/reducers/messageReducer";
import { useTranslation } from "react-i18next";

interface DraggableTabsItemProps {
  id: number;
  tab: TabType | undefined;
  currentTab: number | undefined;
  onTabUpdate: (
    tabID: number,
    name: string,
    value: string,
    position: number
  ) => void;
  onTabChange: (itemID: number) => void;
  onDelete: (itemID: number) => void;
}

interface TabValue {
  name: string;
  value: string;
}

const DraggableTabsItem = (props: DraggableTabsItemProps) => {
  const { id, tab, currentTab, onTabChange, onTabUpdate, onDelete } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [newTabValue, setNewTabValue] = useState<TabValue>({
    name: tab?.name ?? "",
    value: tab?.value ?? "",
  });

  const isActive = currentTab === tab?.tabID;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const className = classNames("dui-tabs-edit-item", {
    "dui-tabs-edit-item-active": isActive,
  });

  const handlePageChange = (itemID?: number) => {
    if (itemID !== undefined && currentTab !== undefined) {
      onTabChange(itemID);
    }
  };

  const handleConfirmDelete = () => {
    if (tab?.tabID !== undefined) {
      onDelete(tab?.tabID);
    }
  };

  const handleConfirmChanges = () => {
    if (tab?.tabID === undefined) return;
    if (newTabValue.name === "") {
      sendToasterMessage(
        dispatch,
        t("nameCannotBeEmpty"),
        MessageTypeEnum.Error
      );
      return;
    }
    updateTab(dispatch, tab?.tabID, newTabValue.name, newTabValue.value);
    setIsEditOpen(false);
    onTabUpdate(tab?.tabID, newTabValue.name, newTabValue.value, tab?.position);
    sendToasterMessage(dispatch, "Záložka bola úspešne uložená");
  };

  const handleTabChange = (name: string, value: string) => {
    setNewTabValue({ name, value });
  };

  return (
    <div ref={setNodeRef} style={style} className={className}>
      <div className="dui-tabs-edit-item-buttons">
        <div {...attributes} {...listeners} className="dui-btn-inner">
          <Button
            icon={<UnfoldMoreIcon />}
            type="text"
            className="dui-sort-btn"
          />
        </div>
        <div className="dui-btn-inner">
          <Button
            icon={<Edit />}
            type="text"
            onClick={() => {
              setIsEditOpen(true);
            }}
          />
        </div>
        <div className="dui-btn-inner">
          <Button
            icon={<DeleteOutline />}
            type="text"
            onClick={() => {
              setIsDeleteOpen(true);
            }}
          />
        </div>
      </div>
      <div
        className="dui-tabs-edit-item-name"
        onClick={() => handlePageChange(tab?.tabID)}
      >
        {tab?.name}
      </div>
      <ConfirmDialog
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t("deleteTitle")}
        description={`${t("areYouSureDelete1")} ${tab?.name} ${t("areYouSureDelete2")}`}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        confirmButtonAppearance="error"
      />
      <ConfirmDialog
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onConfirm={handleConfirmChanges}
        title={t("tabEditTitle")}
        description={<EditTab tab={tab} onChange={handleTabChange} />}
        confirmText={t("save")}
        cancelText={t("cancel")}
        confirmButtonAppearance="default"
      />
    </div>
  );
};

export default DraggableTabsItem;
